import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  PiggyBank,
  GraduationCap,
  Network,
  CheckCircle2,
} from "lucide-react";

const services = [
  {
    icon: Building2,
    eyebrow: "01 — Platform",
    title: "SaaS Platform for Schools & Institutions",
    description:
      "A secure, easy-to-adopt platform that lets schools deliver financial literacy programs, track student engagement and measure outcomes — without the operational overhead.",
    highlights: [
      "Institution dashboard for admins and educators",
      "Student enrollment and role-based access",
      "Progress tracking and engagement analytics",
      "Modular curriculum deployment",
    ],
  },
  {
    icon: PiggyBank,
    eyebrow: "02 — Banking",
    title: "Rewards-Based Student Banking Ecosystem",
    description:
      "A points-driven, gamified banking experience that teaches students the fundamentals of saving, earning and responsible spending — in a safe, school-aligned environment.",
    highlights: [
      "Points-based rewards and goal-setting",
      "Real-world analogs for saving and spending",
      "Age-appropriate interactions and controls",
      "Transparent insights for students and schools",
    ],
  },
  {
    icon: GraduationCap,
    eyebrow: "03 — Curriculum",
    title: "Financial Literacy Programs for K-12",
    description:
      "Structured, age-based learning journeys — from early primary introductions through confident secondary-school modules — designed to make money concepts tangible.",
    highlights: [
      "Curriculum aligned to K-12 grade levels",
      "Interactive lessons and real-world scenarios",
      "Assessments and progress milestones",
      "Educator-friendly delivery resources",
    ],
  },
  {
    icon: Network,
    eyebrow: "04 — Delivery",
    title: "Hybrid Education Delivery Model",
    description:
      "A blended learning approach that combines digital simulations with in-person workshops — giving schools the flexibility to teach financial literacy their way.",
    highlights: [
      "Online modules and live facilitation",
      "Classroom-ready lesson plans and activities",
      "Integration with existing school timetables",
      "Scalable across multiple institutions",
    ],
  },
];

export const ServicesPage = () => {
  return (
    <main data-testid="services-page">
      {/* HEADER */}
      <section
        data-testid="services-header-section"
        className="bg-white border-b border-slate-200"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 md:pt-28 pb-16 md:pb-20">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-500 mb-5">
              Our Core Offerings
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 leading-[1.05]">
              Solutions built to make every student financially confident.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-600 leading-relaxed">
              A focused portfolio of platforms, programs and partnerships —
              designed for schools, institutions and the students they serve.
            </p>
          </div>
        </div>
      </section>

      {/* SERVICES LIST */}
      <section
        data-testid="services-list-section"
        className="py-16 md:py-24 bg-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          {services.map((s, idx) => (
            <article
              key={s.title}
              data-testid={`service-card-${idx}`}
              className="rounded-2xl border border-slate-200 bg-white hover:border-slate-300 hover:shadow-md transition-all overflow-hidden"
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
                <div className="lg:col-span-4 p-8 md:p-10 bg-slate-50 border-b lg:border-b-0 lg:border-r border-slate-200 flex flex-col justify-between">
                  <div>
                    <div className="w-12 h-12 rounded-lg bg-slate-900 text-white flex items-center justify-center mb-6">
                      <s.icon className="w-5 h-5" />
                    </div>
                    <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-500">
                      {s.eyebrow}
                    </p>
                    <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-900 leading-tight">
                      {s.title}
                    </h2>
                  </div>
                </div>
                <div className="lg:col-span-8 p-8 md:p-10">
                  <p className="text-base md:text-lg leading-relaxed text-slate-600">
                    {s.description}
                  </p>
                  <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                    {s.highlights.map((h) => (
                      <li
                        key={h}
                        className="flex items-start gap-2.5 text-sm text-slate-700"
                      >
                        <CheckCircle2 className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <div className="max-w-2xl">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-400 mb-4">
                Ready to Begin
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
                Bring financial literacy into your institution.
              </h2>
            </div>
            <Link
              to="/contact"
              data-testid="services-cta-contact"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-amber-500 text-slate-900 px-6 py-3.5 text-base font-semibold hover:bg-amber-400 transition-colors"
            >
              Talk to Us
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ServicesPage;
